import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { ArrowRight, Check, ChevronDown, CheckCircle2 } from 'lucide-react';
import { Container, Glow, Section, SectionHeader } from './ui';
import { Reveal, Stagger, StaggerItem, fadeUp } from './motion';
import SmartLink from './SmartLink';

export { Container, Section, SectionHeader, Reveal, Stagger, StaggerItem };

// Accent palette shared by icon chips across pages.
const ACCENTS = {
  brand: 'border-brand-400/20 bg-brand-400/10 text-brand-300',
  emerald: 'border-emerald-400/20 bg-emerald-400/10 text-emerald-300',
  violet: 'border-violet-400/20 bg-violet-400/10 text-violet-300',
  amber: 'border-amber-400/20 bg-amber-400/10 text-amber-300',
  pink: 'border-pink-400/20 bg-pink-400/10 text-pink-300',
  rose: 'border-rose-400/20 bg-rose-400/10 text-rose-300',
  blue: 'border-blue-400/20 bg-blue-400/10 text-blue-300',
};

export const IconChip = ({ icon: Icon, accent = 'brand', size = 'md', className }) => (
  <span
    className={clsx(
      'flex shrink-0 items-center justify-center border',
      size === 'sm' && 'h-9 w-9 rounded-xl',
      size === 'md' && 'h-11 w-11 rounded-xl',
      size === 'lg' && 'h-14 w-14 rounded-2xl',
      ACCENTS[accent],
      className
    )}
  >
    <Icon className={size === 'lg' ? 'h-6 w-6' : size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} />
  </span>
);

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */

export const ButtonLink = ({ to, variant = 'primary', children, className }) => (
  <SmartLink to={to} className={clsx(variant === 'primary' ? 'btn-primary' : 'btn-secondary', 'group', className)}>
    {children}
    {variant === 'primary' && (
      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
    )}
  </SmartLink>
);

export const TextLink = ({ to, children, className }) => (
  <SmartLink to={to} className={clsx('link-arrow group', className)}>
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
  </SmartLink>
);

/* ------------------------------------------------------------------ */
/* Page hero                                                           */
/* ------------------------------------------------------------------ */

// `highlight` renders in the brand gradient on its own line above `title`.
export const PageHero = ({ eyebrow, highlight, title, description, children, meta }) => (
  <section className="relative overflow-hidden pb-16 pt-40 sm:pb-24 sm:pt-48">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute inset-0 bg-grid-fade bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
      <Glow className="left-1/2 top-[-14rem] h-[32rem] w-[56rem] -translate-x-1/2 bg-brand-500/20" />
    </div>
    <Container className="relative">
      <Stagger className="mx-auto max-w-4xl text-center" stagger={0.1}>
        {(eyebrow || meta) && (
          <StaggerItem className="flex flex-wrap items-center justify-center gap-3">
            {eyebrow && <span className="eyebrow">{eyebrow}</span>}
            {meta && <span className="text-sm text-slate-500">{meta}</span>}
          </StaggerItem>
        )}
        <StaggerItem
          as="h1"
          className="mt-6 text-5xl font-extrabold leading-[1.02] tracking-tightest sm:text-6xl lg:text-7xl"
        >
          {highlight && (
            <>
              <span className="text-gradient-brand pr-[0.06em]">{highlight}</span>
              {title && <br />}
            </>
          )}
          {title && <span className="text-gradient pr-[0.06em]">{title}</span>}
        </StaggerItem>
        {description && (
          <StaggerItem
            as="p"
            className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-slate-400 sm:text-xl sm:leading-relaxed"
          >
            {description}
          </StaggerItem>
        )}
        {children && (
          <StaggerItem className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            {children}
          </StaggerItem>
        )}
      </Stagger>
    </Container>
  </section>
);

/* ------------------------------------------------------------------ */
/* Content blocks                                                      */
/* ------------------------------------------------------------------ */

// Large intro statement — e.g. the paragraph that follows a section header.
export const Lead = ({ children, className }) => (
  <Reveal className={clsx('mx-auto max-w-3xl text-center', className)}>
    <p className="text-lg leading-relaxed text-slate-300 sm:text-xl sm:leading-relaxed">{children}</p>
  </Reveal>
);

// Items may be strings or { label, text }; labels render bold ("Security: No surprises…").
export const CheckList = ({ items, className, accent = 'brand' }) => (
  <ul className={clsx('space-y-3', className)}>
    {items.map((item) => {
      const label = typeof item === 'string' ? null : item.label;
      const text = typeof item === 'string' ? item : item.text;
      return (
        <li key={label || text} className="flex items-start gap-3 text-slate-300">
          <span
            className={clsx(
              'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
              accent === 'emerald' ? 'bg-emerald-400/15 text-emerald-300' : 'bg-brand-400/10 text-brand-300'
            )}
          >
            <Check className="h-3 w-3" strokeWidth={3} />
          </span>
          <span className="leading-relaxed">
            {label && <span className="font-semibold text-white">{label}: </span>}
            {text}
          </span>
        </li>
      );
    })}
  </ul>
);

export const Card = ({ className, children, hover = true, as = 'article' }) => {
  const Component = motion[as];
  return (
    <Component
      variants={fadeUp}
      whileHover={hover ? { y: -4 } : undefined}
      transition={{ type: 'spring', stiffness: 300, damping: 26 }}
      className={clsx('surface flex flex-col p-6 transition-colors duration-300 sm:p-8', hover && 'hover:border-white/[0.16]', className)}
    >
      {children}
    </Component>
  );
};

// Icon + title + body card. Use inside <CardGrid> so it staggers in.
export const FeatureCard = ({ icon, accent, title, children, className, footer }) => (
  <Card className={className}>
    {icon && <IconChip icon={icon} accent={accent} />}
    <h3 className={clsx('text-xl font-bold tracking-tight text-white', icon && 'mt-6')}>{title}</h3>
    {children && <div className="mt-3 flex-1 leading-relaxed text-slate-400">{children}</div>}
    {footer && <div className="mt-6">{footer}</div>}
  </Card>
);

export const CardGrid = ({ cols = 3, className, children }) => (
  <Stagger
    className={clsx(
      'grid gap-4 lg:gap-5',
      cols === 2 && 'md:grid-cols-2',
      cols === 3 && 'md:grid-cols-2 lg:grid-cols-3',
      cols === 4 && 'sm:grid-cols-2 lg:grid-cols-4',
      className
    )}
    stagger={0.08}
  >
    {children}
  </Stagger>
);

// Large alternating feature row: text on one side, any visual on the other.
export const FeatureRow = ({ icon, accent, eyebrow, title, children, visual, reverse = false, id }) => (
  <div id={id} className="grid scroll-mt-32 items-center gap-10 lg:grid-cols-2 lg:gap-16">
    <Reveal className={clsx(reverse && 'lg:order-2')}>
      <div className="flex items-center gap-3">
        {icon && <IconChip icon={icon} accent={accent} />}
        {eyebrow && <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{eyebrow}</span>}
      </div>
      <h3 className="mt-6 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{title}</h3>
      <div className="mt-5 space-y-5 text-lg leading-relaxed text-slate-400">{children}</div>
    </Reveal>
    <Reveal delay={0.12} className={clsx(reverse && 'lg:order-1')}>
      {visual}
    </Reveal>
  </div>
);

// Numbered steps laid out horizontally on desktop.
export const Steps = ({ steps, className }) => (
  <Stagger
    as="ol"
    className={clsx(
      'grid gap-4 sm:grid-cols-2 lg:gap-5',
      steps.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3',
      className
    )}
    stagger={0.1}
  >
    {steps.map((step, i) => (
      <motion.li key={step.title} variants={fadeUp} className="surface flex flex-col p-6 sm:p-7">
        <span className="text-sm font-semibold tabular-nums text-brand-300">0{i + 1}</span>
        <h3 className="mt-4 text-lg font-bold tracking-tight text-white">{step.title}</h3>
        {step.text && <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{step.text}</p>}
        {step.extra}
      </motion.li>
    ))}
  </Stagger>
);

export const FAQ = ({ items, className }) => {
  const [open, setOpen] = useState(0);
  return (
    <Stagger className={clsx('mx-auto max-w-3xl space-y-3', className)} stagger={0.06}>
      {items.map((item, i) => {
        const isOpen = open === i;
        const panelId = `faq-panel-${i}`;
        return (
          <motion.div key={item.q} variants={fadeUp} className={clsx('surface !rounded-2xl transition-colors', isOpen && '!border-white/15')}>
            <h3>
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="flex min-h-[44px] w-full cursor-pointer items-center justify-between gap-4 px-6 py-5 text-left font-semibold text-white"
              >
                {item.q}
                <ChevronDown className={clsx('h-5 w-5 shrink-0 text-slate-400 transition-transform duration-300', isOpen && 'rotate-180')} />
              </button>
            </h3>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  id={panelId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 leading-relaxed text-slate-400">{item.a}</div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </Stagger>
  );
};

// Closing call-to-action panel used at the bottom of most pages.
export const CTABanner = ({ title, description, children, note }) => (
  <section className="relative py-24 sm:py-32">
    <Container>
      <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-b from-ink-800 to-ink-950 px-6 py-20 text-center sm:px-16 sm:py-24">
        <div aria-hidden="true" className="absolute inset-0 bg-grid-fade bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black_10%,transparent_70%)]" />
        <Glow className="left-1/2 top-0 h-80 w-[40rem] -translate-x-1/2 -translate-y-1/2 bg-brand-500/30" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-300/60 to-transparent" />
        <Stagger className="relative mx-auto max-w-3xl" stagger={0.1}>
          <StaggerItem as="h2" className="text-gradient text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            {title}
          </StaggerItem>
          {description && (
            <StaggerItem as="p" className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-slate-400">
              {description}
            </StaggerItem>
          )}
          {children && (
            <StaggerItem className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
              {children}
            </StaggerItem>
          )}
          {note && (
            <StaggerItem as="p" className="mt-8 text-sm text-slate-500">
              {note}
            </StaggerItem>
          )}
        </Stagger>
      </div>
    </Container>
  </section>
);

/* ------------------------------------------------------------------ */
/* Forms                                                               */
/* ------------------------------------------------------------------ */

const fieldClass =
  'w-full rounded-xl border border-white/10 bg-ink-900/80 px-4 py-3 text-base text-white placeholder:text-slate-600 transition-colors duration-200 hover:border-white/20 focus:border-brand-400/60 focus:outline-none focus:ring-2 focus:ring-brand-400/30';

export const Field = ({ label, id, as = 'input', options, className, ...rest }) => (
  <div className={className}>
    <label htmlFor={id} className="mb-2 block text-sm font-medium text-slate-300">
      {label}
    </label>
    {as === 'select' ? (
      <select id={id} name={id} className={clsx(fieldClass, 'cursor-pointer')} {...rest}>
        {options.map((o) => (
          <option key={o} value={o} className="bg-ink-900">
            {o}
          </option>
        ))}
      </select>
    ) : as === 'textarea' ? (
      <textarea id={id} name={id} rows={5} className={clsx(fieldClass, 'resize-y')} {...rest} />
    ) : (
      <input id={id} name={id} className={fieldClass} {...rest} />
    )}
  </div>
);

// Front-end only: there is no backend yet, so submitting just shows a confirmation.
export const ContactForm = ({ title, subjectLabel = 'Subject', subjects, consent, submitLabel = 'Send Message', className }) => {
  const initial = { firstName: '', lastName: '', email: '', subject: subjects[0], message: '', consent: false };
  const [form, setForm] = useState(initial);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!sent) return undefined;
    const t = setTimeout(() => setSent(false), 6000);
    return () => clearTimeout(t);
  }, [sent]);

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    setForm(initial);
  };

  return (
    <form onSubmit={submit} className={clsx('surface p-6 sm:p-10', className)}>
      {title && <h3 className="mb-8 text-2xl font-bold tracking-tight text-white">{title}</h3>}
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="First Name" id="firstName" value={form.firstName} onChange={update} required autoComplete="given-name" />
        <Field label="Last Name" id="lastName" value={form.lastName} onChange={update} required autoComplete="family-name" />
        <Field label="Email" id="email" type="email" value={form.email} onChange={update} required autoComplete="email" className="sm:col-span-2" />
        <Field label={subjectLabel} id="subject" as="select" options={subjects} value={form.subject} onChange={update} className="sm:col-span-2" />
        <Field label="Message" id="message" as="textarea" value={form.message} onChange={update} required className="sm:col-span-2" />
      </div>
      {consent && (
        <label htmlFor="consent" className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-slate-400">
          <input
            id="consent"
            name="consent"
            type="checkbox"
            checked={form.consent}
            onChange={update}
            required
            className="mt-0.5 h-4 w-4 cursor-pointer rounded border-white/20 bg-ink-900 accent-brand-400"
          />
          {consent}
        </label>
      )}
      <button type="submit" className="btn-primary mt-8 w-full sm:w-auto">
        {submitLabel}
      </button>
      <div aria-live="polite">
        <AnimatePresence>
          {sent && (
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-5 flex items-center gap-2 text-sm font-medium text-emerald-300"
            >
              <CheckCircle2 className="h-4 w-4" />
              Message sent! We'll get back to you soon.
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </form>
  );
};

/* ------------------------------------------------------------------ */
/* Legal documents (Privacy, Terms)                                     */
/* ------------------------------------------------------------------ */

// Typography for long-form legal content. Use plain h3/p/ul/li inside.
export const Prose = ({ children, className }) => (
  <div
    className={clsx(
      'space-y-4 leading-relaxed text-slate-400',
      '[&_a]:font-medium [&_a]:text-brand-300 [&_a]:underline-offset-4 hover:[&_a]:underline',
      '[&_h3]:pt-4 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-white',
      '[&_li]:pl-1 [&_strong]:font-semibold [&_strong]:text-slate-200',
      '[&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_ul]:marker:text-brand-400/60',
      className
    )}
  >
    {children}
  </div>
);

// sections: [{ id, title, content }]. Renders a sticky table of contents with scrollspy.
export const LegalDocument = ({ sections }) => {
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -65% 0px' }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  return (
    <Container className="pb-24 sm:pb-32">
      <div className="grid gap-10 lg:grid-cols-[16rem_1fr] lg:gap-16">
        <nav aria-label="Contents" className="hidden lg:block">
          <div className="sticky top-32">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Contents</p>
            <ul className="space-y-1 border-l border-white/[0.08]">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className={clsx(
                      '-ml-px block border-l py-1.5 pl-4 text-sm transition-colors duration-200',
                      active === s.id ? 'border-brand-400 font-medium text-white' : 'border-transparent text-slate-500 hover:text-slate-300'
                    )}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="min-w-0 space-y-6">
          {sections.map((s) => (
            <Reveal key={s.id}>
              <section id={s.id} className="surface scroll-mt-32 p-6 sm:p-10">
                <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{s.title}</h2>
                <Prose className="mt-6">{s.content}</Prose>
              </section>
            </Reveal>
          ))}
        </div>
      </div>
    </Container>
  );
};
